import { UniverseImagePipe } from '@/domain/universes/pipes/universe-image.pipe';
import type { UniverseId } from '@/domain/universes/universe.interface';
import { UniverseService } from '@/domain/universes/universe.service';
import type { Period } from '@/interfaces/Building.interface';
import { BusinessIntelligenceDataSourceCode } from '@/interfaces/BusinessIntelligence.interface';
import type { ClassificationNodeId } from '@/interfaces/Classification.interface';
import { UiAvatarComponent } from '@/ui/avatar/ui-avatar';
import { UiButtonComponent } from '@/ui/button/ui-button';
import { UiIconButtonComponent } from '@/ui/icon-button/ui-icon-button';
import { UiSectionActionsDirective } from '@/ui/section/section-actions/ui-section-actions';
import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { UiTableModule } from '@/ui/table/ui-table.module';
import { ReferenceListLabelPipe } from '@/utils/reference-lists/pipes/reference-list-label.pipe';
import { ReferenceListName } from '@/utils/reference-lists/reference-lists.interface';
import { ReferenceListsService } from '@/utils/reference-lists/reference-lists.service';
import { significantDigitConversion } from '@/utils/toolkit/numbers/significant-digits.utils';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { Component, inject, Input, type OnInit } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { combineLatest, NEVER, type Observable } from 'rxjs';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { PeriodIdentifier } from '../../constants/period.constant';
import { AdvizeoTooltipDirective } from '../../directives/advizeo-tooltip/advizeo-tooltip.directive';
import { TimeseriesIndicatorCode } from '../../interfaces/Timeseries.interface';
import type { UnitFormatter } from '../../interfaces/Unit.interface';
import type { BalanceTableConfig } from '../../interfaces/Widget.interface';
import { SharedUiColoredTextColorDirective } from '../../libs/shared/ui/theming/lib/colors/shared-ui-color.directives';
import { FormatUnitPipe } from '../../pipes/format-unit.pipe';
import { LocalNumberPipe, SignificantDigitPipe } from '../../pipes/localnumber.pipe';
import { TranslatePeriodPipe } from '../../pipes/translate-period.pipe';
import { CsvExportProvider } from '../../providers/csv-export/csv-export';
import { DashboardingProvider } from '../../providers/dashboarding.provider';
import { UnitProvider } from '../../providers/unit/unit.provider';
import { Page, RoutingService } from '../../services/routing/routing.service';
import { DeveloperHelperComponent } from '../developer-helper/developer-helper';
import { HelpMeComponent } from '../help-me/help-me';
import { LoadingAndErrorComponent } from '../loading-and-error/loading-and-error';
import { LegacyDashboardsProvider } from './../../providers/dashboards/legacy-dashboards.provider';

interface TableDatasource {
    isTotal: boolean;
    universeId: UniverseId | null;
    measured: number;
    estimated: number;
    savings: number;
    savingsPercentage: number | null;
    ratioByFloorSurface: number | null;
    ratioByPeopleCount: number | null;
    rank: number;
}

@UntilDestroy()
@Component({
    styleUrls: ['./energy-balance-table.scss'],
    selector: 'energy-balance-table',
    templateUrl: 'energy-balance-table.html',
    imports: [
        UiSectionComponent,
        UiSectionActionsDirective,
        UiIconButtonComponent,
        AdvizeoTooltipDirective,
        UiButtonComponent,
        UiSectionContentComponent,
        DeveloperHelperComponent,
        UiTableModule,
        UiAvatarComponent,
        SharedUiColoredTextColorDirective,
        HelpMeComponent,
        LoadingAndErrorComponent,
        AsyncPipe,
        TranslatePipe,
        LocalNumberPipe,
        SignificantDigitPipe,
        FormatUnitPipe,
        UniverseImagePipe,
        TranslatePeriodPipe,
        ReferenceListLabelPipe
    ]
})
export class EnergyBalanceTableComponent implements OnInit {
    private _translate = inject(TranslateService);
    private _routingService = inject(RoutingService);
    private _csvExportProvider = inject(CsvExportProvider);
    private _referenceListsService = inject(ReferenceListsService);
    private _dashboardingProvider = inject(DashboardingProvider);
    private _unitProvider = inject(UnitProvider);
    private _legacyDashboardsProvider = inject(LegacyDashboardsProvider);
    private _universeService = inject(UniverseService);

    @Input() config!: BalanceTableConfig;

    headers$!: Observable<string[]>;
    dataSource: TableDatasource[] = [];
    dataSource$!: Observable<TableDatasource[]>;
    period$!: Observable<PeriodIdentifier | null>;
    lastTwelveMonths = PeriodIdentifier.LAST_TWELVE_MONTHS;

    developerHelperDataSourceCode = BusinessIntelligenceDataSourceCode.CONSUMPTION;

    private _ignoreEstimated: boolean = false;

    isLoading: boolean = true;
    loadingError: boolean = false;

    valueFormatter: UnitFormatter = () => '';
    peoplePerformanceValueFormatter: UnitFormatter = () => '';
    surfacePerformanceValueFormatter: UnitFormatter = () => '';

    ngOnInit() {
        this.headers$ = this._dashboardingProvider.isEstimatedIgnoredOnWidgets$().pipe(
            map(ignoreEstimated => {
                if (ignoreEstimated) {
                    return ['type', 'universe', 'actualConsumption', 'referenceGap', 'surfacePerformance', 'peoplePerformance'];
                } else {
                    return [
                        'type',
                        'universe',
                        'actualConsumption',
                        'estimatedConsumption',
                        'referenceGap',
                        'surfacePerformance',
                        'peoplePerformance'
                    ];
                }
            })
        );

        this.period$ = this._dashboardingProvider.getSelectedPeriodIdentifier$();

        this.dataSource$ = combineLatest([
            this._dashboardingProvider.getMeaningfulNodeIdAndSelectedPeriod$(),
            this._dashboardingProvider.isEstimatedIgnoredOnWidgets$()
        ]).pipe(
            tap(([_, ignoreEstimated]) => {
                this.isLoading = true;
                this._ignoreEstimated = ignoreEstimated;
            }),
            switchMap(([[classificationNodeId, period], ignoreEstimated]) => this.getData$(classificationNodeId, period, ignoreEstimated)),
            catchError(() => {
                this.isLoading = false;
                this.loadingError = true;
                return NEVER;
            }),
            map(result => {
                this.isLoading = false;
                return result;
            }),
            tap(result => this.initializeFormatters(result)),
            tap(result => (this.dataSource = result)),
            shareReplay({ refCount: true, bufferSize: 1 })
        );
    }

    private getData$(classificationNodeId: ClassificationNodeId, period: Period, ignoreEstimated: boolean): Observable<TableDatasource[]> {
        const normalizedPeriod = this._dashboardingProvider.normalizePeriodTo12FullMonths(period);
        return this._legacyDashboardsProvider
            .getEnergyBalanceTableData$(
                classificationNodeId,
                this.config.timeseriesIndicator,
                !ignoreEstimated,
                normalizedPeriod.start,
                normalizedPeriod.end
            )
            .pipe(
                map(result => {
                    this.isLoading = false;

                    const dataSource: TableDatasource[] = result.ipeByUniverse
                        .map(ipe => {
                            const universe = this._universeService.getUniverseR(ipe.universeId)!;
                            return {
                                isTotal: false,
                                universeId: ipe.universeId,
                                measured: ipe.measured,
                                estimated: ipe.estimated,
                                savings: ipe.savings,
                                savingsPercentage: ipe.savingsPercentage,
                                ratioByFloorSurface: ipe.ratioByFloorSurface,
                                ratioByPeopleCount: ipe.ratioByPeopleCount,
                                rank: universe.rank
                            };
                        })
                        .sort((a, b) => a.rank - b.rank);

                    if (this.config.timeseriesIndicator !== TimeseriesIndicatorCode.WATER_CONSUMPTION) {
                        dataSource.push({
                            isTotal: true,
                            universeId: null,
                            measured: result.ipeTotal.measured,
                            estimated: result.ipeTotal.estimated,
                            savings: result.ipeTotal.savings,
                            savingsPercentage: result.ipeTotal.savingsPercentage,
                            ratioByFloorSurface: result.ipeTotal.ratioByFloorSurface,
                            ratioByPeopleCount: result.ipeTotal.ratioByPeopleCount,
                            rank: 99999
                        });
                    }

                    return dataSource;
                })
            );
    }

    private initializeFormatters(dataByUniverse: TableDatasource[]) {
        const baseUnitCode = this._unitProvider.getDeliveryPointTimeseriesUnitCode(this.config.timeseriesIndicator);

        this.valueFormatter = this._unitProvider.formatterFactory(
            baseUnitCode,
            this._unitProvider.getBestConversion(
                [
                    ...dataByUniverse.map(rawData => rawData.measured).filter(measured => isNotNullish(measured) && measured !== 0),
                    ...dataByUniverse.map(rawData => rawData.estimated).filter(estimated => isNotNullish(estimated) && estimated !== 0),
                    ...dataByUniverse.map(rawData => rawData.savings).filter(savings => isNotNullish(savings) && savings !== 0)
                ],
                baseUnitCode
            )
        );

        const performanceValueFormatter = this._unitProvider.formatterFactory(
            baseUnitCode,
            this._unitProvider.getBestConversion(
                [
                    ...(dataByUniverse
                        .map(rawData => rawData.ratioByFloorSurface)
                        .filter(ratioByFloorSurface => isNotNullish(ratioByFloorSurface) && ratioByFloorSurface !== 0) as number[]),
                    ...(dataByUniverse
                        .map(rawData => rawData.ratioByPeopleCount)
                        .filter(ratioByPeople => isNotNullish(ratioByPeople) && ratioByPeople !== 0) as number[])
                ],
                baseUnitCode
            )
        );

        this.surfacePerformanceValueFormatter = (value: number) =>
            this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.SURFACE_PERFORMANCE_UNIT', { unit: performanceValueFormatter(value) });
        this.peoplePerformanceValueFormatter = (value: number) =>
            this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.PEOPLE_PERFORMANCE_UNIT', { unit: performanceValueFormatter(value) });
    }

    goToBillsByBuilding() {
        this._routingService.goTo(Page.BILLS_BY_BUILDING, {
            indicatorCode: this.config.timeseriesIndicator,
            dashboardRight: this._dashboardingProvider.getSelectedLegacyDashboard()!.dashboardRight
        });
    }

    exportDataAsCsv() {
        this._csvExportProvider.downloadCsvFromDataSource(this.dataSource, [
            {
                header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.UNIVERSE'),
                value: rowData => {
                    if (rowData.isTotal) {
                        return this._translate.instant('STUFF.TOTAL');
                    } else {
                        return this._referenceListsService.getReferenceListItemLabelR(ReferenceListName.UNIVERSE, rowData.universeId!);
                    }
                }
            },
            {
                header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.ENERGY_UNIT'),
                value: () =>
                    this._referenceListsService.getReferenceListItemLabelR(
                        ReferenceListName.UNIT,
                        this._unitProvider.getDeliveryPointTimeseriesUnitCode(this.config.timeseriesIndicator)
                    )
            },
            {
                header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.ACTUAL_CONSUMPTION'),
                value: rowData => significantDigitConversion(rowData.measured)
            },
            ...(this._ignoreEstimated
                ? []
                : [
                      {
                          header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.ESTIMATED_CONSUMPTION'),
                          value: (rowData: any) => significantDigitConversion(rowData.estimated)
                      }
                  ]),
            {
                header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.REFERENCE_GAP'),
                value: rowData => significantDigitConversion(rowData.savings)
            },
            {
                header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.SURFACE_PERFORMANCE'),
                value: rowData => significantDigitConversion(rowData.ratioByFloorSurface)
            },
            {
                header: this._translate.instant('WIDGET.ENERGY_BALANCE_TABLE.PEOPLE_PERFORMANCE'),
                value: rowData => significantDigitConversion(rowData.ratioByPeopleCount)
            }
        ]);
    }
}
